import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  useUpdateReferral,
  type Referral,
  type PPRDocuments,
  type HospitalDocuments,
  type AssessmentFlagKey,
  type PaperAnamneseForms,
  type SchoolCompletionForms,
  ASSESSMENT_SCHEME_ITEMS,
  applyAssessmentToggle,
  assessmentSchemeItemKey,
  findAssessmentRespondentGroup,
  isAssessmentParentOn,
  isAssessmentRespondentOn,
} from '@/hooks/useReferrals';
import { Input } from '@/components/ui/Input';
import { DynamicTextFields } from '@/components/ui/DynamicTextFields';
import { CheckboxWithFiles } from '@/components/referrals/CheckboxWithFiles';

interface ReferralSagsakterTabProps {
  referral: Referral;
}

export function ReferralSagsakterTab({ referral }: ReferralSagsakterTabProps) {
  const { t } = useTranslation();
  const updateReferral = useUpdateReferral();
  const pending = updateReferral.isPending;

  async function toggleInfoField(
    field: 'consent_collected' | 'info_letter_sent' | 'agreement_made' | 'payment_arranged',
  ) {
    const currentValue = referral[field];
    const newValue = !currentValue;

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        [field]: newValue,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function togglePprDocument(field: keyof PPRDocuments) {
    const currentValue = referral.ppr_documents?.[field];
    const newValue = !currentValue;

    if (field === 'andet' && currentValue && !newValue) {
      const items = referral.ppr_documents?.andet_items ?? [];
      const hasContent = items.some((item) => item.trim() !== '');
      if (hasContent) {
        toast.error('Tøm alle felter før du fjerner checkmark');
        return;
      }
    }

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      const updated = {
        ...referral.ppr_documents,
        [field]: newValue,
        ...(field === 'andet' && newValue ? { andet_items: [''] } : {}),
      };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        ppr_documents: updated,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function updatePprDocumentText(items: string[]) {
    try {
      const updated = { ...referral.ppr_documents, andet_items: items };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        ppr_documents: updated,
      });
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function toggleHospitalDocument(field: keyof HospitalDocuments) {
    const currentValue = referral.hospital_documents?.[field];
    const newValue = !currentValue;

    if (field === 'andet' && currentValue && !newValue) {
      const items = referral.hospital_documents?.andet_items ?? [];
      const hasContent = items.some((item) => item.trim() !== '');
      if (hasContent) {
        toast.error('Tøm alle felter før du fjerner checkmark');
        return;
      }
    }

    if (field === 'scannings_svar' && currentValue && !newValue) {
      const hasNestedChecks =
        referral.hospital_documents?.scannings_svar_mr ||
        referral.hospital_documents?.scannings_svar_ct ||
        referral.hospital_documents?.scannings_svar_andet;
      if (hasNestedChecks) {
        toast.error('Fjern alle nested checkmarks først');
        return;
      }
    }

    if (field === 'scannings_svar_andet' && currentValue && !newValue) {
      const items = referral.hospital_documents?.scannings_svar_andet_items ?? [];
      const hasContent = items.some((item) => item.trim() !== '');
      if (hasContent) {
        toast.error('Tøm alle felter før du fjerner checkmark');
        return;
      }
    }

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      const updated = {
        ...referral.hospital_documents,
        [field]: newValue,
        ...(field === 'andet' && newValue ? { andet_items: [''] } : {}),
        ...(field === 'scannings_svar_andet' && newValue
          ? { scannings_svar_andet_items: [''] }
          : {}),
      };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        hospital_documents: updated,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function updateHospitalDocumentText(items: string[]) {
    try {
      const updated = { ...referral.hospital_documents, andet_items: items };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        hospital_documents: updated,
      });
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function updateHospitalScanningsAndetText(items: string[]) {
    try {
      const updated = {
        ...referral.hospital_documents,
        scannings_svar_andet_items: items,
      };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        hospital_documents: updated,
      });
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function toggleAssessmentScheme(field: AssessmentFlagKey) {
    const group = findAssessmentRespondentGroup(field);
    let currentValue = Boolean(referral.assessment_schemes?.[field]);
    if (group && field === group.parent) {
      currentValue = isAssessmentParentOn(referral.assessment_schemes, group);
    } else if (group && group.respondents.some((r) => r.field === field)) {
      currentValue = isAssessmentRespondentOn(referral.assessment_schemes, field, group);
    }
    const newValue = !currentValue;

    if (group && field === group.parent && currentValue && !newValue) {
      const hasNested = group.respondents.some((r) =>
        isAssessmentRespondentOn(referral.assessment_schemes, r.field, group),
      );
      if (hasNested) {
        toast.error('Fjern alle nested checkmarks først');
        return;
      }
    }

    if (field === 'andet' && currentValue && !newValue) {
      const items = referral.assessment_schemes?.andet_items ?? [];
      const hasContent = items.some((item) => item.trim() !== '');
      if (hasContent) {
        toast.error('Tøm alle felter før du fjerner checkmark');
        return;
      }
    }

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      const updated = {
        ...applyAssessmentToggle(referral.assessment_schemes ?? {}, field, newValue),
        ...(field === 'andet' && newValue ? { andet_items: [''] } : {}),
      };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        assessment_schemes: updated,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function updateAssessmentAndetText(items: string[]) {
    try {
      const updated = { ...referral.assessment_schemes, andet_items: items };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        assessment_schemes: updated,
      });
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function togglePaperAnamnese(field: keyof PaperAnamneseForms) {
    const currentValue = referral.paper_anamnese_forms?.[field];
    const newValue = !currentValue;

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      const updated = { ...referral.paper_anamnese_forms, [field]: newValue };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        paper_anamnese_forms: updated,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function toggleSchoolCompletion(field: keyof SchoolCompletionForms) {
    const currentValue = referral.school_completion_forms?.[field];
    const newValue = !currentValue;

    if (currentValue && !newValue) {
      if (!confirm('Er du sikker på du vil fjerne denne markering?')) {
        return;
      }
    }

    try {
      const updated = { ...referral.school_completion_forms, [field]: newValue };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        school_completion_forms: updated,
      });
      toast.success(t('referrals.toast_updated'));
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  async function updateOtherDocuments(value: string) {
    try {
      const updated = { ...referral.other_documents, requested_items: value };
      await updateReferral.mutateAsync({
        referral_id: referral.id,
        other_documents: updated,
      });
    } catch (err) {
      console.error('update referral failed', err);
      toast.error(t('referrals.update_error'));
    }
  }

  const infoProgress = [
    referral.consent_collected,
    referral.info_letter_sent,
    referral.agreement_made,
    referral.payment_arranged,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Combined Documents Section */}
      <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <h2 className="text-xl font-semibold text-ds-text mb-6">
          Følgende ønskes kontaktet for rekvirering af sagsakter
        </h2>
    
    <div className="space-y-6">
      {/* PPR and Hospital Grid */}
      <div className="grid gap-8 lg:grid-cols-2">
        {/* PPR Documents */}
        <div>
          <h3 className="text-base font-medium text-ds-text mb-3">PPR</h3>
          <Input
            value={referral.ppr_documents?.institution_name ?? ''}
            onChange={(e) => {
              const updated = { ...referral.ppr_documents, institution_name: e.target.value };
              updateReferral.mutateAsync({
                referral_id: referral.id,
                ppr_documents: updated,
              });
            }}
            placeholder="Angiv by, evt. også afdeling eller kontaktperson"
            className="mb-4"
          />
          <div className="space-y-3 pl-6">
            <CheckboxWithFiles
              checked={referral.ppr_documents?.wisc_v_forside ?? false}
              onChange={() => togglePprDocument('wisc_v_forside')}
              disabled={pending}
              label="WISC V forside eller computerudskrift"
              referralId={referral.id}
              clinicId={referral.clinic_id}
              category="ppr"
              fieldName="wisc_v_forside"
              patientId={referral.patient_id}
            />
            <CheckboxWithFiles
              checked={referral.ppr_documents?.wisc_v_rapport ?? false}
              onChange={() => togglePprDocument('wisc_v_rapport')}
              disabled={pending}
              label="WISC V rapport"
              referralId={referral.id}
              clinicId={referral.clinic_id}
              category="ppr"
              fieldName="wisc_v_rapport"
              patientId={referral.patient_id}
            />
            <CheckboxWithFiles
              checked={referral.ppr_documents?.wppsi_iv_forside ?? false}
              onChange={() => togglePprDocument('wppsi_iv_forside')}
              disabled={pending}
              label="WPPSI-IV forside"
              referralId={referral.id}
              clinicId={referral.clinic_id}
              category="ppr"
              fieldName="wppsi_iv_forside"
              patientId={referral.patient_id}
            />
            <CheckboxWithFiles
              checked={referral.ppr_documents?.wppsi_iv_rapport ?? false}
              onChange={() => togglePprDocument('wppsi_iv_rapport')}
              disabled={pending}
              label="WPPSI-IV rapport"
              referralId={referral.id}
              clinicId={referral.clinic_id}
              category="ppr"
              fieldName="wppsi_iv_rapport"
              patientId={referral.patient_id}
            />
            <CheckboxRow
              checked={referral.ppr_documents?.andet ?? false}
              onChange={() => togglePprDocument('andet')}
              disabled={pending}
              label="Andet"
            />
            {referral.ppr_documents?.andet && (
              <div className="ml-8 mt-2">
                <DynamicTextFields
                  values={referral.ppr_documents?.andet_items ?? []}
                  onChange={(items) => updatePprDocumentText(items)}
                  placeholder="Beskriv..."
                  disabled={pending}
                />
              </div>
            )}
          </div>
        </div>

        {/* Hospital Documents */}
        <div>
          <h3 className="text-base font-medium text-ds-text mb-3">Hospital</h3>
          <Input
            value={referral.hospital_documents?.institution_name ?? ''}
            onChange={(e) => {
              const updated = { ...referral.hospital_documents, institution_name: e.target.value };
              updateReferral.mutateAsync({
                referral_id: referral.id,
                hospital_documents: updated,
              });
            }}
            placeholder="Angiv hvilket, afdeling og årstal"
            className="mb-4"
          />
          <div className="space-y-3 pl-6">
            <CheckboxWithFiles
              checked={referral.hospital_documents?.journaludskrift ?? false}
              onChange={() => toggleHospitalDocument('journaludskrift')}
              disabled={pending}
              label="Journaludskrift"
              referralId={referral.id}
              clinicId={referral.clinic_id}
              category="hospital"
              fieldName="journaludskrift"
              patientId={referral.patient_id}
            />
            
            {/* Expandable Scannings-svar section */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={referral.hospital_documents?.scannings_svar ?? false}
                  onChange={() => toggleHospitalDocument('scannings_svar')}
                  disabled={pending}
                  className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                />
                <span className="text-sm text-ds-text">Scannings-svar</span>
                <ChevronRight 
                  className={`h-4 w-4 text-ds-muted transition-transform duration-200 ease-in-out ${
                    referral.hospital_documents?.scannings_svar ? 'rotate-90' : ''
                  }`}
                />
              </label>
              
              {/* Nested options with smooth transition */}
              <div 
                className={`
                  overflow-hidden transition-all duration-200 ease-in-out
                  ${referral.hospital_documents?.scannings_svar ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}
                `}
              >
                <div className="ml-6 pl-4 border-l-2 border-ds-line space-y-2">
                  <CheckboxWithFiles
                    checked={referral.hospital_documents?.scannings_svar_mr ?? false}
                    onChange={() => toggleHospitalDocument('scannings_svar_mr')}
                    disabled={pending}
                    label="MR"
                    referralId={referral.id}
                    clinicId={referral.clinic_id}
                    category="hospital"
                    fieldName="scannings_svar_mr"
              patientId={referral.patient_id}
                  />
                  <CheckboxWithFiles
                    checked={referral.hospital_documents?.scannings_svar_ct ?? false}
                    onChange={() => toggleHospitalDocument('scannings_svar_ct')}
                    disabled={pending}
                    label="CT"
                    referralId={referral.id}
                    clinicId={referral.clinic_id}
                    category="hospital"
                    fieldName="scannings_svar_ct"
              patientId={referral.patient_id}
                  />
                  <CheckboxRow
                    checked={referral.hospital_documents?.scannings_svar_andet ?? false}
                    onChange={() => toggleHospitalDocument('scannings_svar_andet')}
                    disabled={pending}
                    label="Andet"
                  />
                  {referral.hospital_documents?.scannings_svar_andet && (
                    <div className="mt-2 animate-in fade-in duration-200">
                      <DynamicTextFields
                        values={referral.hospital_documents?.scannings_svar_andet_items ?? []}
                        onChange={(items) => updateHospitalScanningsAndetText(items)}
                        placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
                        disabled={pending}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <CheckboxRow
              checked={referral.hospital_documents?.andet ?? false}
              onChange={() => toggleHospitalDocument('andet')}
              disabled={pending}
              label="Andet"
            />
            {referral.hospital_documents?.andet && (
              <div className="ml-8 mt-2">
                <DynamicTextFields
                  values={referral.hospital_documents?.andet_items ?? []}
                  onChange={(items) => updateHospitalDocumentText(items)}
                  placeholder="Beskriv..."
                  disabled={pending}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Other Documents */}
      <div className="mt-8">
        <h3 className="text-base font-medium text-ds-text mb-4">Andre</h3>
        <textarea
          value={referral.other_documents?.requested_items ?? ''}
          onChange={(e) => updateOtherDocuments(e.target.value)}
          rows={3}
          placeholder="Angiv hvem og hvad der ønskes, Husk årstal"
          className="block w-full rounded-lg border border-ds-field bg-ds-card px-4 py-3 text-sm focus:border-ds-teal focus:outline-none focus:ring-2 focus:ring-ds-teal focus:ring-offset-0"
        />
      </div>
    </div>
  </section>

  {/* Forms sent with invitation - wrapper section */}
  <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
    <h2 className="text-xl font-semibold text-ds-text mb-2">
      Følgende medsendes indkaldelse
    </h2>
    <p className="text-sm text-ds-muted mb-6">
      derudover sendes altid Anamnese, samtykkeerklæring hjem/skole/institution samt vejkort
    </p>

  {/* Grid: Papir Anamnese + Assessments side by side */}
  <div className="grid gap-6 lg:grid-cols-2">
    <div className="space-y-6">
    {/* Paper Anamnesis */}
    <div>
      <h3 className="text-base font-medium text-ds-text mb-4">
        Papir Anamneseskema
      </h3>
              <div className="space-y-3">
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.smaaboern ?? false}
onChange={() => togglePaperAnamnese('smaaboern')}
disabled={pending}
label="Småbørn"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="smaaboern"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.smaaboern_genhenvisning ?? false}
onChange={() => togglePaperAnamnese('smaaboern_genhenvisning')}
disabled={pending}
label="Småbørn genhenvisning"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="smaaboern_genhenvisning"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.skoleboern ?? false}
onChange={() => togglePaperAnamnese('skoleboern')}
disabled={pending}
label="Skolebørn"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="skoleboern"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.skoleboern_genhenvisning ?? false}
onChange={() => togglePaperAnamnese('skoleboern_genhenvisning')}
disabled={pending}
label="Skolebørn genhenvisning"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="skoleboern_genhenvisning"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.unge_15_plus ?? false}
onChange={() => togglePaperAnamnese('unge_15_plus')}
disabled={pending}
label="Unge 15+"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="unge_15_plus"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.unge_15_plus_genhenvisning ?? false}
onChange={() => togglePaperAnamnese('unge_15_plus_genhenvisning')}
disabled={pending}
label="Unge 15+ genhenvisning"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="unge_15_plus_genhenvisning"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.tillaeg_til_anamnese_13_18 ?? false}
onChange={() => togglePaperAnamnese('tillaeg_til_anamnese_13_18')}
disabled={pending}
label="Tillæg til anamneseskema (13-18 år)"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="tillaeg_til_anamnese_13_18"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.nordisk_sporgeskema_2_5 ?? false}
onChange={() => togglePaperAnamnese('nordisk_sporgeskema_2_5')}
disabled={pending}
label="Nordisk spørgeskema (2-5 år)"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="nordisk_sporgeskema_2_5"
              patientId={referral.patient_id}
              />
              <CheckboxWithFiles
checked={referral.paper_anamnese_forms?.dupaul_foraeldre_skole ?? false}
onChange={() => togglePaperAnamnese('dupaul_foraeldre_skole')}
disabled={pending}
label="DUPAUL forældre // skole"
referralId={referral.id}
clinicId={referral.clinic_id}
category="paper_anamnese"
fieldName="dupaul_foraeldre_skole"
              patientId={referral.patient_id}
              />
            </div>
    </div>

    {/* School Completion Forms */}
    <div>
      <h3 className="text-base font-medium text-ds-text mb-4">
        Skema til udfyldelse af førskole/skole
      </h3>
      <div className="space-y-3">
        <CheckboxWithFiles
          checked={referral.school_completion_forms?.førskole ?? false}
          onChange={() => toggleSchoolCompletion('førskole')}
          disabled={pending}
          label="Førskole"
          referralId={referral.id}
          clinicId={referral.clinic_id}
          category="school_completion"
          fieldName="førskole"
              patientId={referral.patient_id}
        />
        <CheckboxWithFiles
          checked={referral.school_completion_forms?.indskoling ?? false}
          onChange={() => toggleSchoolCompletion('indskoling')}
          disabled={pending}
          label="Indskoling"
          referralId={referral.id}
          clinicId={referral.clinic_id}
          category="school_completion"
          fieldName="indskoling"
              patientId={referral.patient_id}
        />
        <CheckboxWithFiles
          checked={referral.school_completion_forms?.mellemtrin ?? false}
          onChange={() => toggleSchoolCompletion('mellemtrin')}
          disabled={pending}
          label="Mellemtrin"
          referralId={referral.id}
          clinicId={referral.clinic_id}
          category="school_completion"
          fieldName="mellemtrin"
              patientId={referral.patient_id}
        />
        <CheckboxWithFiles
          checked={referral.school_completion_forms?.udskoling ?? false}
          onChange={() => toggleSchoolCompletion('udskoling')}
          disabled={pending}
          label="Udskoling"
          referralId={referral.id}
          clinicId={referral.clinic_id}
          category="school_completion"
          fieldName="udskoling"
              patientId={referral.patient_id}
        />
      </div>
    </div>
    </div>

    {/* Assessments */}
    <div>
      <h3 className="text-base font-medium text-ds-text mb-4">
        Elektroniske skemaer (Assessments)
      </h3>
              <div className="space-y-3">
              {ASSESSMENT_SCHEME_ITEMS.map((item) => {
                switch (item.kind) {
                  case 'group': {
                    const group = item.group;
                    const parentOn = isAssessmentParentOn(referral.assessment_schemes, group);
                    return (
                      <div key={assessmentSchemeItemKey(item)}>
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={parentOn}
                            onChange={() => toggleAssessmentScheme(group.parent)}
                            disabled={pending}
                            className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                          />
                          <span className="text-sm text-ds-text">{group.title}</span>
                          <ChevronRight
                            className={`h-4 w-4 text-ds-muted transition-transform duration-200 ease-in-out ${
                              parentOn ? 'rotate-90' : ''
                            }`}
                          />
                        </label>
                        <div
                          className={`
                        overflow-hidden transition-all duration-200 ease-in-out
                        ${parentOn ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'}
                      `}
                        >
                          <div className="ml-6 pl-4 border-l-2 border-ds-line space-y-2">
                            {group.respondents.map((respondent) => (
                              <CheckboxWithFiles
                                key={respondent.field}
                                checked={isAssessmentRespondentOn(
                                  referral.assessment_schemes,
                                  respondent.field,
                                  group,
                                )}
                                onChange={() => toggleAssessmentScheme(respondent.field)}
                                disabled={pending}
                                label={respondent.label}
                                referralId={referral.id}
                                clinicId={referral.clinic_id}
                                category="assessment"
                                fieldName={respondent.field}
                                patientId={referral.patient_id}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  case 'flag':
                    return (
                      <CheckboxWithFiles
                        key={assessmentSchemeItemKey(item)}
                        checked={referral.assessment_schemes?.[item.field] ?? false}
                        onChange={() => toggleAssessmentScheme(item.field)}
                        disabled={pending}
                        label={item.label}
                        referralId={referral.id}
                        clinicId={referral.clinic_id}
                        category="assessment"
                        fieldName={item.field}
                        patientId={referral.patient_id}
                      />
                    );
                  case 'andet':
                    return (
                      <div key={assessmentSchemeItemKey(item)}>
                        <CheckboxRow
                          checked={referral.assessment_schemes?.andet ?? false}
                          onChange={() => toggleAssessmentScheme('andet')}
                          disabled={pending}
                          label={item.label}
                        />
                        {referral.assessment_schemes?.andet && (
                          <div className="ml-8 mt-2">
                            <DynamicTextFields
                              values={referral.assessment_schemes?.andet_items ?? []}
                              onChange={(items) => updateAssessmentAndetText(items)}
                              placeholder="Angiv andre assessments..."
                              disabled={pending}
                            />
                          </div>
                        )}
                      </div>
                    );
                  default: {
                    const _exhaustive: never = item;
                    return _exhaustive;
                  }
                }
              })}
  </div>
  </div>
  </div>
  </section>

  {/* Info Collection Section - last */}
  <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-medium text-ds-text">
        {t('referrals.info_collection_title')}
      </h2>
      <span className="text-sm font-medium text-ds-muted">
        {infoProgress}/4
      </span>
    </div>
    <div className="mt-4 flex flex-wrap gap-4">
      <CheckboxRow
        checked={referral.consent_collected}
        onChange={() => toggleInfoField('consent_collected')}
        disabled={pending}
        label={t('referrals.consent_collected')}
      />
      <CheckboxRow
        checked={referral.info_letter_sent}
        onChange={() => toggleInfoField('info_letter_sent')}
        disabled={pending}
        label={t('referrals.info_letter_sent')}
      />
      <CheckboxRow
        checked={referral.agreement_made}
        onChange={() => toggleInfoField('agreement_made')}
        disabled={pending}
        label={t('referrals.agreement_made')}
      />
      <CheckboxRow
        checked={referral.payment_arranged}
        onChange={() => toggleInfoField('payment_arranged')}
        disabled={pending}
        label={t('referrals.payment_arranged')}
      />
    </div>
  </section>
</div>

  );
}

function CheckboxRow({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <label className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
      />
      <span className="text-sm text-ds-text">{label}</span>
    </label>
  );
}
